'use client';

import React, { useContext, createContext } from 'react';

interface UITextContextType {
  getButtonText: (key: string, fallback: string) => string;
  getStatusText: (key: string, fallback: string) => string;
  getProgressText: (key: string, fallback: string) => string;
  getActionText: (key: string, fallback: string, template?: Record<string, string>) => string;
}

const UITextContext = createContext<UITextContextType | undefined>(undefined);

type UITextProviderProps = {
  children: React.ReactNode;
  uiText?: Record<string, string>;
};

export const UITextProvider = ({ children, uiText = {} }: UITextProviderProps) => {
  const getButtonText = (key: string, fallback: string): string => {
    return uiText[`button.${key}`] || fallback;
  };

  const getStatusText = (key: string, fallback: string): string => {
    return uiText[`status.${key}`] || fallback;
  };

  const getProgressText = (key: string, fallback: string): string => {
    return uiText[`progress.${key}`] || fallback;
  };

  const getActionText = (
    key: string,
    fallback: string,
    template?: Record<string, string>
  ): string => {
    let text = uiText[`action.${key}`] || fallback;
    if (template) {
      Object.entries(template).forEach(([placeholder, value]) => {
        text = text.replace(`{${placeholder}}`, value || '');
      });
    }
    return text;
  };

  return React.createElement(
    UITextContext.Provider,
    { value: { getButtonText, getStatusText, getProgressText, getActionText } },
    children
  );
};

export const useUIText = (): UITextContextType => {
  const context = useContext(UITextContext);
  if (!context) {
    // Fallback if context not provided
    return {
      getButtonText: (_key: string, fallback: string) => fallback,
      getStatusText: (_key: string, fallback: string) => fallback,
      getProgressText: (_key: string, fallback: string) => fallback,
      getActionText: (_key: string, fallback: string) => fallback,
    };
  }
  return context;
};
